import { zodResolver } from "@hookform/resolvers/zod";
import { RegisterRequest } from "@playarena/shared";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import type React from "react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";

import { getUserRoles } from "@/utils/formatters";

import { useAuth } from "../../../hooks/useAuth";
import { ROUTES, COUNTRIES } from "../../../utils/constants";
import { registerSchema } from "../../../utils/validators";
import { Alert, AlertDescription } from "../../ui/alert";
import { Button } from "../../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";

const RegisterForm: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { register: registerUser, isLoading, error } = useAuth();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<RegisterRequest>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      role: "fan",
    },
  });

  const onSubmit = async (data: RegisterRequest) => {
    try {
      await registerUser(data);
      navigate(ROUTES.HOME);
    } catch (_err) {
      // Error is handled by the auth slice
    }
  };

  return (
    <Card className='w-full max-w-md'>
      <CardHeader className='space-y-1'>
        <CardTitle className='text-center text-2xl font-bold'>Sign Up</CardTitle>
        <CardDescription className='text-center'>
          Create your account to get started
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
          {error && (
            <Alert variant='destructive'>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className='space-y-2'>
            <Label htmlFor='name'>Full Name</Label>
            <Input
              id='name'
              type='text'
              placeholder='Enter your full name'
              {...register("name")}
              className={errors.name ? "border-destructive" : ""}
            />
            {errors.name && <p className='text-sm text-destructive'>{errors.name.message}</p>}
          </div>

          <div className='space-y-2'>
            <Label htmlFor='email'>Email</Label>
            <Input
              id='email'
              type='email'
              placeholder='Enter your email'
              {...register("email")}
              className={errors.email ? "border-destructive" : ""}
            />
            {errors.email && <p className='text-sm text-destructive'>{errors.email.message}</p>}
          </div>

          <div className='space-y-2'>
            <Label htmlFor='phone'>Phone (Optional)</Label>
            <Input
              id='phone'
              type='tel'
              placeholder='Enter your phone number'
              {...register("phone")}
              className={errors.phone ? "border-destructive" : ""}
            />
            {errors.phone && <p className='text-sm text-destructive'>{errors.phone.message}</p>}
          </div>

          <div className='space-y-2'>
            <Label htmlFor='country'>Country</Label>
            <Select onValueChange={value => setValue("country", value)}>
              <SelectTrigger className={errors.country ? "border-destructive" : ""}>
                <SelectValue placeholder='Select your country' />
              </SelectTrigger>
              <SelectContent>
                {COUNTRIES.map(country => (
                  <SelectItem key={country.code} value={country.code}>
                    {country.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.country && <p className='text-sm text-destructive'>{errors.country.message}</p>}
          </div>

          <div className='space-y-2'>
            <Label htmlFor='role'>Role</Label>
            <Select onValueChange={value => setValue("role", value as any)} defaultValue='fan'>
              <SelectTrigger className={errors.role ? "border-destructive" : ""}>
                <SelectValue placeholder='Select your role' />
              </SelectTrigger>
              <SelectContent>
                {getUserRoles({ hiddenRoles: ["superadmin"] }).map(roleConfig => (
                  <SelectItem key={roleConfig.role} value={roleConfig.role}>
                    {roleConfig.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.role && <p className='text-sm text-destructive'>{errors.role.message}</p>}
          </div>

          <div className='space-y-2'>
            <Label htmlFor='password'>Password</Label>
            <div className='relative'>
              <Input
                id='password'
                type={showPassword ? "text" : "password"}
                placeholder='Enter your password'
                {...register("password")}
                className={errors.password ? "border-destructive pr-10" : "pr-10"}
              />
              <Button
                type='button'
                variant='ghost'
                size='sm'
                className='absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent'
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff className='h-4 w-4' /> : <Eye className='h-4 w-4' />}
              </Button>
            </div>
            {errors.password && (
              <p className='text-sm text-destructive'>{errors.password.message}</p>
            )}
          </div>

          <div className='space-y-2'>
            <Label htmlFor='confirmPassword'>Confirm Password</Label>
            <div className='relative'>
              <Input
                id='confirmPassword'
                type={showConfirmPassword ? "text" : "password"}
                placeholder='Confirm your password'
                {...register("confirmPassword")}
                className={errors.confirmPassword ? "border-destructive pr-10" : "pr-10"}
              />
              <Button
                type='button'
                variant='ghost'
                size='sm'
                className='absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent'
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <EyeOff className='h-4 w-4' /> : <Eye className='h-4 w-4' />}
              </Button>
            </div>
            {errors.confirmPassword && (
              <p className='text-sm text-destructive'>{errors.confirmPassword.message}</p>
            )}
          </div>

          <Button type='submit' className='w-full' disabled={isLoading}>
            {isLoading && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
            Sign Up
          </Button>
        </form>

        <div className='mt-6 text-center text-sm'>
          Already have an account?{" "}
          <Link to={ROUTES.LOGIN} className='text-primary hover:underline'>
            Sign in
          </Link>
        </div>
      </CardContent>
    </Card>
  );
};

export default RegisterForm;
